import { inject, Injectable } from '@angular/core';
import { EMPTY, expand, Observable, reduce } from 'rxjs';
import { API_BASE_URL } from '@/app/core/http/api-base-url.token';
import { AuthHttpClient } from '@/app/core/http/auth-http-client';
import { StaffCatalogOption, StaffType } from '../models/staff.model';
import {
  StaffCatalogApiResponse,
  StaffDireccionAtencionApiItem,
  StaffEspecialidadApiItem,
} from './staff-catalog-api.types';
import { hasMorePages, toCatalogOptions, toCatalogQueryParams } from './staff-catalog.mapper';
import { StaffCatalogRepository } from './staff-catalog.repository';

const MAX_CATALOG_PAGES = 20;

@Injectable()
export class StaffCatalogHttpRepository extends StaffCatalogRepository {
  private readonly http = inject(AuthHttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  override listSpecialties(type: StaffType): Observable<StaffCatalogOption[]> {
    return this.fetchCatalog<StaffEspecialidadApiItem>('especialidades', type, (item) => ({
      id: item.idEspecialidad,
      label: item.especialidad,
    }));
  }

  override listAddresses(type: StaffType): Observable<StaffCatalogOption[]> {
    return this.fetchCatalog<StaffDireccionAtencionApiItem>(
      'direcciones-atencion',
      type,
      (item) => ({
        id: item.idGlosaDireccion,
        label: item.glosaDireccion,
      }),
    );
  }

  private fetchCatalog<T>(
    path: string,
    type: StaffType,
    toOption: (item: T) => StaffCatalogOption,
  ): Observable<StaffCatalogOption[]> {
    return this.fetchCatalogPage<T>(path, type, 1).pipe(
      expand((response) => this.fetchNextPage<T>(path, type, response)),
      reduce<StaffCatalogApiResponse<T>, StaffCatalogOption[]>(
        (options, response) => [...options, ...toCatalogOptions(response, toOption)],
        [],
      ),
    );
  }

  private fetchNextPage<T>(
    path: string,
    type: StaffType,
    response: StaffCatalogApiResponse<T>,
  ): Observable<StaffCatalogApiResponse<T>> {
    if (!hasMorePages(response.meta)) return EMPTY;
    if (response.meta.page >= MAX_CATALOG_PAGES) {
      console.warn(
        `Catálogo de ${path} truncado en ${MAX_CATALOG_PAGES} páginas — el prestador tiene más registros de los que se cargaron.`,
      );
      return EMPTY;
    }
    return this.fetchCatalogPage<T>(path, type, response.meta.page + 1);
  }

  private fetchCatalogPage<T>(
    path: string,
    type: StaffType,
    page: number,
  ): Observable<StaffCatalogApiResponse<T>> {
    return this.http.get<StaffCatalogApiResponse<T>>(`${this.baseUrl}/staff/${path}`, {
      params: toCatalogQueryParams(type, page),
    });
  }
}
